/**
 * Cost-Effective Shipping Calculator
 * Uses region-based flat rate pricing
 */

// State to Region mapping
const STATE_TO_REGION = {
  // North Central = ₦3,000
  'FCT': 'NORTH_CENTRAL',
  'Federal Capital Territory': 'NORTH_CENTRAL',
  'Abuja': 'NORTH_CENTRAL',
  'Benue': 'NORTH_CENTRAL',
  'Kogi': 'NORTH_CENTRAL',
  'Kwara': 'NORTH_CENTRAL',
  'Nasarawa': 'NORTH_CENTRAL',
  'Niger': 'NORTH_CENTRAL',
  'Plateau': 'NORTH_CENTRAL',
  
  // South West = ₦5,000
  'Lagos': 'SOUTH_WEST',
  'Ogun': 'SOUTH_WEST',
  'Oyo': 'SOUTH_WEST',
  'Osun': 'SOUTH_WEST',
  'Ondo': 'SOUTH_WEST',
  'Ekiti': 'SOUTH_WEST',
  
  // South East = ₦5,000
  'Abia': 'SOUTH_EAST',
  'Anambra': 'SOUTH_EAST',
  'Ebonyi': 'SOUTH_EAST',
  'Enugu': 'SOUTH_EAST',
  'Imo': 'SOUTH_EAST',
  
  // South South = ₦5,000
  'Akwa Ibom': 'SOUTH_SOUTH',
  'Bayelsa': 'SOUTH_SOUTH',
  'Cross River': 'SOUTH_SOUTH',
  'Delta': 'SOUTH_SOUTH',
  'Edo': 'SOUTH_SOUTH',
  'Rivers': 'SOUTH_SOUTH',
  
  // North West = ₦3,000
  'Jigawa': 'NORTH_WEST',
  'Kaduna': 'NORTH_WEST',
  'Kano': 'NORTH_WEST',
  'Katsina': 'NORTH_WEST',
  'Kebbi': 'NORTH_WEST',
  'Sokoto': 'NORTH_WEST',
  'Zamfara': 'NORTH_WEST',
  
  // North East = ₦3,000
  'Adamawa': 'NORTH_EAST',
  'Bauchi': 'NORTH_EAST',
  'Borno': 'NORTH_EAST',
  'Gombe': 'NORTH_EAST',
  'Taraba': 'NORTH_EAST',
  'Yobe': 'NORTH_EAST',
};

// Region-based flat rate pricing (in NGN)
const REGION_PRICING = {
  'NORTH_CENTRAL': 3000,
  'SOUTH_WEST': 5000,
  'SOUTH_EAST': 5000,
  'SOUTH_SOUTH': 5000,
  'NORTH_WEST': 3000,
  'NORTH_EAST': 3000,
};

// Default shipping cost for unmapped states (use highest rate)
const DEFAULT_SHIPPING_COST = 5000;

/**
 * Get delivery region from state name
 * @param {string} state - State name
 * @returns {string} Region name, or null if not found
 */
export const getRegionFromState = (state) => {
  if (!state) return null;
  
  // Normalize state name (trim)
  const normalizedState = state.trim();
  
  // Try exact match first
  if (STATE_TO_REGION[normalizedState]) {
    return STATE_TO_REGION[normalizedState];
  }
  
  // Try case-insensitive match
  const stateKey = Object.keys(STATE_TO_REGION).find(
    key => key.toLowerCase() === normalizedState.toLowerCase()
  );
  
  if (stateKey) {
    return STATE_TO_REGION[stateKey];
  }
  
  // Return null for unmapped states
  return null;
};

/**
 * Get appropriate shipping method based on total weight
 * @param {number} totalWeight - Total weight in kg
 * @returns {string} Vehicle type (BIKE, VAN, TRUCK)
 */
export const getShippingMethod = (totalWeight) => {
  if (totalWeight < 5) return 'BIKE';
  if (totalWeight < 20) return 'VAN';
  return 'TRUCK';
};

/**
 * Calculate shipping cost based on state (flat rate per region)
 * @param {Array} items - Cart items array (not used for calculation, kept for compatibility)
 * @param {string} state - Destination state
 * @param {string} vehicleType - Vehicle type (kept for compatibility, not used in calculation)
 * @returns {number} Shipping cost in NGN
 */
export const calculateShippingCost = (items, state, vehicleType = null) => {
  if (!state) return 0;
  
  // Get region from state
  const region = getRegionFromState(state);
  
  if (!region) {
    // Default to highest rate for unmapped states
    return DEFAULT_SHIPPING_COST;
  }
  
  // Return flat rate for the region
  return REGION_PRICING[region] || DEFAULT_SHIPPING_COST;
};

/**
 * Get estimated delivery time based on region
 * @param {string} state - Destination state
 * @returns {string} Estimated delivery time range
 */
export const getEstimatedDelivery = (state) => {
  const region = getRegionFromState(state);
  
  // Delivery time estimates by region
  const deliveryTimeEstimates = {
    'NORTH_CENTRAL': "2-3 days",   // North Central
    'SOUTH_WEST': "3-5 days",      // South West
    'SOUTH_EAST': "3-5 days",      // South East
    'SOUTH_SOUTH': "3-5 days",     // South South
    'NORTH_WEST': "3-5 days",      // North West
    'NORTH_EAST': "3-5 days",      // North East
  };
  
  return deliveryTimeEstimates[region] || "3-5 days";
};

/**
 * Get shipping method details
 * @param {number} totalWeight - Total weight in kg
 * @returns {Object} Shipping method details
 */
export const getShippingMethodDetails = (totalWeight) => {
  const method = getShippingMethod(totalWeight);
  const methodNames = {
    BIKE: "Bike Delivery",
    VAN: "Van Delivery",
    TRUCK: "Truck Delivery",
  };
  
  return {
    code: method,
    name: methodNames[method] || "Standard Delivery",
    minWeight: method === 'BIKE' ? 0 : method === 'VAN' ? 5 : 20,
  };
};

/**
 * Check if free shipping applies (optional feature)
 * @param {number} subtotal - Order subtotal
 * @param {number} freeShippingThreshold - Minimum order amount for free shipping (default: 50000)
 * @returns {boolean}
 */
export const isFreeShipping = (subtotal, freeShippingThreshold = 50000) => {
  return subtotal >= freeShippingThreshold;
};

